import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { X, Search, Link as LinkIcon } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import {
  ModalOverlay as Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter
} from './ModalStyles';
import { ThemedText } from './ThemedText';
import { Button } from './Button';
import { ErrorState } from './ErrorState';
import { TableContainer, Table, Thead, Th, Tbody, Tr, Td } from './TableStyles';
import { StyledSearchContainer, SearchIcon, TransparentInput } from './SearchStyles';
import formSubmissionService from '../api/services/formSubmissionService';

/**
 * Modal picker for linking an existing submission to a parent as a
 * repeated-form child. Lists submissions of the target schema, supports
 * search and multi-select, then calls POST /children/link for each
 * selection on confirm.
 *
 * Props:
 *   isOpen       boolean — controls visibility.
 *   onClose      () => void — close handler.
 *   parentId     number — the parent submission that will receive the link.
 *   fieldId      string — the parent schema's RepeatedForm field key.
 *   schemaId     number — RepeatedFormSchemaId of the target field.
 *   schemaName   string — display name for the target schema.
 *   existingChildIds  number[] — ids already attached; pre-disables them.
 *   onLinked     (linkedCount) => void — fired once all links succeed.
 */
export const LinkExistingSubmissionPicker = ({
  isOpen,
  onClose,
  parentId,
  fieldId,
  schemaId,
  schemaName,
  existingChildIds = [],
  onLinked,
  onSelect
}) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const existing = useMemo(() => new Set(existingChildIds), [existingChildIds]);

  useEffect(() => {
    if (!isOpen || !schemaId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setSelected(new Set());
    setSearch('');
    formSubmissionService
      .getSubmissions({ formSchemaId: schemaId, pageSize: 100, includeChildren: true })
      .then(response => {
        if (cancelled) return;
        const items = Array.isArray(response) ? response : (response.items ?? []);
        setSubmissions(items);
      })
      .catch(err => {
        if (!cancelled) setError(err?.message || 'Failed to load submissions');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [isOpen, schemaId]);

  const filtered = useMemo(() => {
    if (!search.trim()) return submissions;
    const q = search.trim().toLowerCase();
    return submissions.filter(s => {
      const idMatch = String(s.id).includes(q);
      const nameMatch = (s.submitterName ?? '').toLowerCase().includes(q);
      const emailMatch = (s.submitterEmail ?? '').toLowerCase().includes(q);
      return idMatch || nameMatch || emailMatch;
    });
  }, [submissions, search]);

  const toggle = useCallback((id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleConfirm = async () => {
    if (selected.size === 0) return;
    setSubmitting(true);
    setError(null);
    try {
      if (onSelect) {
        const selectedSubmissions = submissions.filter(s => selected.has(s.id));
        onSelect(selectedSubmissions);
        onClose?.();
      } else {
        let linked = 0;
        for (const id of selected) {
          await formSubmissionService.linkExistingChild(parentId, {
            childSubmissionId: id,
            fieldId
          });
          linked += 1;
        }
        onLinked?.(linked);
        onClose?.();
      }
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Failed to link submissions');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal $isOpen={isOpen} onClick={onClose}>
      <ModalContent theme={theme} onClick={e => e.stopPropagation()} style={{ maxWidth: 720, width: '90%' }}>
        <ModalHeader theme={theme}>
          <ThemedText type="subtitle" style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}>
            <LinkIcon size={18} /> Link existing {schemaName || 'submission'}
          </ThemedText>
          <Button variant="ghost" onClick={onClose} aria-label="Close">
            <X size={18} />
          </Button>
        </ModalHeader>

        <ModalBody theme={theme}>
          <ThemedText style={{ color: theme.colors.muted, marginBottom: theme.spacing.md }}>
            Select one or more submissions to attach to this parent. Already-linked rows are shown as disabled.
          </ThemedText>

          <StyledSearchContainer theme={theme} style={{ marginBottom: theme.spacing.md }}>
            <SearchIcon><Search size={16} /></SearchIcon>
            <TransparentInput
              theme={theme}
              placeholder="Search by id, name, or email"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </StyledSearchContainer>

          {loading ? (
            <ThemedText>Loading…</ThemedText>
          ) : error ? (
            <ErrorState theme={theme} title="Couldn't load submissions" message={error} />
          ) : filtered.length === 0 ? (
            <ThemedText style={{ color: theme.colors.muted }}>No submissions found.</ThemedText>
          ) : (
            <TableContainer theme={theme}>
              <Table theme={theme}>
                <Thead theme={theme}>
                  <Tr>
                    <Th theme={theme} style={{ width: 48 }}></Th>
                    <Th theme={theme}>ID</Th>
                    <Th theme={theme}>Submitter</Th>
                    <Th theme={theme}>Status</Th>
                    <Th theme={theme}>Created</Th>
                  </Tr>
                </Thead>
                <Tbody theme={theme}>
                  {filtered.map(s => {
                    const alreadyLinked = existing.has(s.id);
                    const isSelected = selected.has(s.id);
                    return (
                      <Tr
                        key={s.id}
                        theme={theme}
                        style={{
                          opacity: alreadyLinked ? 0.5 : 1,
                          cursor: alreadyLinked ? 'not-allowed' : 'pointer'
                        }}
                        onClick={() => { if (!alreadyLinked) toggle(s.id); }}
                      >
                        <Td theme={theme}>
                          <input
                            type="checkbox"
                            checked={isSelected || alreadyLinked}
                            disabled={alreadyLinked}
                            onChange={() => toggle(s.id)}
                            onClick={e => e.stopPropagation()}
                          />
                        </Td>
                        <Td theme={theme}>#{s.id}</Td>
                        <Td theme={theme}>
                          {s.submitterName || s.submitterEmail || <span style={{ color: theme.colors.muted }}>—</span>}
                        </Td>
                        <Td theme={theme}>{s.status}</Td>
                        <Td theme={theme}>
                          {s.createdAt ? new Date(s.createdAt).toLocaleDateString() : '—'}
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </TableContainer>
          )}
        </ModalBody>

        <ModalFooter theme={theme}>
          <ThemedText style={{ color: theme.colors.muted, marginRight: 'auto' }}>
            {selected.size > 0
              ? `${selected.size} selected`
              : 'Select at least one submission to link.'}
          </ThemedText>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            disabled={submitting || selected.size === 0}
          >
            {submitting ? 'Linking…' : `Link ${selected.size || ''}`.trim()}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default LinkExistingSubmissionPicker;
